import { useEffect } from 'react';

/**
 * Reveals elements as they scroll into view. Any element tagged with
 * `data-reveal` starts hidden (see `[data-reveal]` in taranaSite.css) and gets
 * `.is-visible` added when it enters the viewport, triggering a fade + slide up.
 *
 * Children tagged with `data-reveal-stagger` are revealed one after another via
 * a per-item `--reveal-i` index, so grids (trio cards, how-it-works steps,
 * bullet lists) cascade instead of popping in all at once.
 *
 * Re-runs whenever `key` changes (pass the route pathname) so freshly mounted
 * pages get observed. Honours `prefers-reduced-motion` by revealing everything
 * immediately with no transition.
 */
export function useScrollReveal(key?: string) {
  useEffect(() => {
    const root = document.querySelector('.tarana-site');
    if (!root) return;

    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (els.length === 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    // Assign a stagger index to each direct child marked for staggering.
    els.forEach((el) => {
      const staggerKids = el.querySelectorAll<HTMLElement>('[data-reveal-stagger]');
      staggerKids.forEach((kid, i) => kid.style.setProperty('--reveal-i', String(i)));
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -3% 0px' }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [key]);
}
